import React from 'react';
import { LayoutAnimation, StyleSheet, TouchableOpacity } from 'react-native';

import { EventInfo } from '@/api/events/types/types';
import EdgeComboIcon from '@/assets/icons/edge-combo-icon.js';
import { LineSeparator } from '@/components/LineSeparator';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { PlayerRow } from '@/feature/lobby/components/PlayerRow';
import { PickSelectionMethods } from '@/feature/lobby/hooks/use-pick-selection';
import { runStoreUpdate } from '@/utils/zustand';

import { betslipPlayerSelector, useBetslipStore } from '../hooks/use-betslip-store';
import { eventUtils } from '../utils/event-utils';
import { useEdgeCombo } from './EdgeComboModalProvider';

export const PlayerPickSlipCard: React.FC<
    BetslipPick & {
        presentation: 'default' | 'sheet';
        shouldDisplayEdgeComboIcon?: boolean;
        shouldDisplaySeparator?: boolean;
        eventDetails: EventInfo;
        edgeComboPlayers?: [BetslipPick, BetslipPick];
        openPlayerProfile?: (playerId: string) => void;
    } & PickSelectionMethods
> = ({
    player: playerFromPickSlip,
    outcome,
    projection,
    presentation,
    shouldDisplayEdgeComboIcon,
    shouldDisplaySeparator,
    eventDetails,
    makeSelection,
    removeSelection,
    edgeComboPlayers,
    openPlayerProfile,
}) => {
    const { showAcceptEdgeComboPairModal } = useEdgeCombo();
    const team = eventUtils.getTeamById(eventDetails, playerFromPickSlip.team?.id);

    const playerWithLatestValues = team?.players?.find(p => p.id === playerFromPickSlip.id);
    const player = {
        ...(playerWithLatestValues || playerFromPickSlip),
        team,
        league: playerFromPickSlip.league,
        sport: playerFromPickSlip.sport,
    };
    const picked = useBetslipStore(
        betslipPlayerSelector({
            eventId: eventDetails.id,
            playerId: player.id,
        })
    );

    const animateRemove = () => {
        LayoutAnimation.configureNext({
            update: { type: 'easeInEaseOut' },
            duration: 300,
        });
        runStoreUpdate(() => {
            picked && removeSelection([{ player: player, eventId: picked.eventId }]);
        });
    };

    const handlePress = () => {
        if (edgeComboPlayers) {
            showAcceptEdgeComboPairModal({
                edgeCombo: edgeComboPlayers,
                displayMode: 'info',
            });
        }
    };

    return (
        <Box>
            <PlayerRow
                mode="pick-slip"
                projection={projection}
                player={player}
                event={eventDetails}
                outcome={outcome}
                showTrash
                pressable
                presentation={presentation}
                onRemoveFromLineup={animateRemove}
                makeSelection={makeSelection}
                removeSelection={removeSelection}
                paddingVertical={'s0'}
                openPlayerProfile={openPlayerProfile}
            />
            {shouldDisplayEdgeComboIcon ? (
                <Row alignItems={'center'} style={styles.edgeComboSeparator}>
                    <TouchableOpacity
                        hitSlop={EDGE_COMBO_ICON_HEIGHT * 2}
                        onPress={handlePress}
                        style={styles.edgeComboIcon}
                    >
                        <EdgeComboIcon
                            color={ds.colorValue.icon.primary}
                            width={EDGE_COMBO_ICON_HEIGHT}
                            height={EDGE_COMBO_ICON_HEIGHT}
                        />
                    </TouchableOpacity>
                    <Box flex={1}>
                        <LineSeparator />
                    </Box>
                </Row>
            ) : shouldDisplaySeparator ? (
                <LineSeparator style={styles.separator} />
            ) : null}
        </Box>
    );
};

const EDGE_COMBO_ICON_HEIGHT = 16;

const styles = StyleSheet.create({
    edgeComboIcon: {
        paddingLeft: 52,
        marginRight: 32,
    },
    separator: {
        marginVertical: 16,
    },
    edgeComboSeparator: {
        marginVertical: 8,
    },
});
